import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckBadgeIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { DatePicker } from '../components/ui/DatePicker';
import { CountryCombobox } from '../components/ui/CountryCombobox';
import { SectionBody } from '../components/trip/SectionBody';
import { FlashCover } from '../components/trip/FlashCover';
import { cn } from '../utils/cn';
import {
  tripBriefsService,
  briefLiveProgressChanged,
  type Accommodation,
  type BriefStatus,
  type DiningReservation,
  type FlightSegment,
  type GroundTransportMode,
  type ItineraryLeg,
  type MeetingVenue,
  type RiskRating,
  type TripBriefRecord,
  type TripClassification,
  type TripPurpose,
} from '../services/trip-briefs.service';
import type { ExecutiveProtectionContextType } from '../layouts/ExecutiveProtectionLayout';
import {
  countryHasSubdivisions,
  emptyForm,
  formToCreateInput,
  isFormValid,
  uid,
  type NewBriefFormState,
} from './tripBriefForm';
import toast from 'react-hot-toast';

const STATUS_BADGES: Record<BriefStatus, string> = {
  DRAFT: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200',
  GENERATING: 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200',
  READY: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
  APPROVED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200',
  ARCHIVED: 'bg-secondary-100 text-secondary-500 dark:bg-secondary-800 dark:text-secondary-400',
};

const RISK_COLORS: Record<RiskRating, string> = {
  LOW: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200',
  MEDIUM: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
  HIGH: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200',
};

const REC_BADGES: Record<string, string> = {
  GO: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200',
  GO_WITH_CAUTION: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
  NO_GO: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200',
};

const PURPOSES: TripPurpose[] = ['BUSINESS', 'BOARD', 'CONFERENCE', 'PERSONAL', 'GOVERNMENT', 'MEDIA', 'OTHER'];
const CLASSIFICATIONS: TripClassification[] = ['INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'];
const GROUND_TRANSPORT_MODES: { value: GroundTransportMode; label: string }[] = [
  { value: 'HIRED_CAR', label: 'Hired Car' },
  { value: 'RIDESHARE', label: 'Rideshare' },
  { value: 'RENTAL', label: 'Rental' },
  { value: 'TAXI', label: 'Taxi' },
  { value: 'PUBLIC_TRANSIT', label: 'Public Transit' },
  { value: 'CLIENT_PROVIDED', label: 'Client Provided' },
  { value: 'OTHER', label: 'Other' },
];

function formatDate(iso: string | null): string {
  if (!iso) {return '—';}
  return iso.slice(0, 10);
}

// 12 minutes, not 5. This guard exists to notice a background job that died
// silently, but it measures staleness of `updatedAt`, which only bumps when a
// section *succeeds*. A single rate-limited AI section can legitimately go
// ~7.75 min without writing anything (4 attempts x 90s timeout, plus 15s/30s/60s
// backoff — see MAX_RETRIES/CALL_TIMEOUT_MS in aiSectionsResolver.ts), and the
// full 14-section run can exceed an hour. At 5 min this fired during normal
// operation, latched `shouldPoll` to false and froze the progress bar on a
// perfectly healthy run — the "scan stuck halfway" report on the demo site.
const AI_SUMMARY_STUCK_MS = 12 * 60 * 1000;

// Measure staleness from updatedAt (resets on create AND re-run) rather than
// createdAt, so a re-run of an existing brief still polls for fresh results.
function isAiSummaryStuck(brief: TripBriefRecord): boolean {
  if (brief.aiSummaryStatus !== 'PENDING') {return false;}
  return Date.now() - new Date(brief.updatedAt).getTime() > AI_SUMMARY_STUCK_MS;
}

interface PersonOption {
  id: string;
  firstName: string;
  lastName: string;
  medicalConditions?: string[];
  specialRequirements?: string;
}

interface NewBriefModalProps {
  open: boolean;
  onClose: () => void;
  protectedPersons: PersonOption[];
  onCreated: (brief: TripBriefRecord) => void;
}

function NewBriefModal({ open, onClose, protectedPersons, onCreated }: NewBriefModalProps) {
  const [form, setForm] = useState<NewBriefFormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLogistics, setShowLogistics] = useState(false);

  if (!open) {return null;}

  const setField = <K extends keyof NewBriefFormState>(key: K, value: NewBriefFormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const updateLeg = (idx: number, patch: Partial<ItineraryLeg>) => {
    setForm((f) => ({
      ...f,
      itinerary: f.itinerary.map((leg, i) => (i === idx ? { ...leg, ...patch } : leg)),
    }));
  };

  const addLeg = () =>
    setForm((f) => ({
      ...f,
      itinerary: [...f.itinerary, { id: uid('leg'), city: '', country: '', startDate: '', endDate: '' }],
    }));

  const removeLeg = (idx: number) =>
    setForm((f) => ({ ...f, itinerary: f.itinerary.filter((_, i) => i !== idx) }));

  // -- Logistics array helpers --
  const addFlight = () =>
    setForm((f) => ({
      ...f,
      flights: [...f.flights, { id: uid('fl'), carrier: '', flightNumber: '', departureAirport: '', arrivalAirport: '', departureTime: '', arrivalTime: '' }],
    }));
  const updateFlight = (idx: number, patch: Partial<FlightSegment>) =>
    setForm((f) => ({ ...f, flights: f.flights.map((fl, i) => (i === idx ? { ...fl, ...patch } : fl)) }));
  const removeFlight = (idx: number) =>
    setForm((f) => ({ ...f, flights: f.flights.filter((_, i) => i !== idx) }));

  const addAccommodation = () =>
    setForm((f) => ({
      ...f,
      accommodations: [...f.accommodations, { id: uid('acc'), name: '', address: '', checkIn: '', checkOut: '' }],
    }));
  const updateAccommodation = (idx: number, patch: Partial<Accommodation>) =>
    setForm((f) => ({ ...f, accommodations: f.accommodations.map((a, i) => (i === idx ? { ...a, ...patch } : a)) }));
  const removeAccommodation = (idx: number) =>
    setForm((f) => ({ ...f, accommodations: f.accommodations.filter((_, i) => i !== idx) }));

  const addMeeting = () =>
    setForm((f) => ({
      ...f,
      meetings: [...f.meetings, { id: uid('mtg'), name: '', address: '', scheduledAt: '', notes: '' }],
    }));
  const updateMeeting = (idx: number, patch: Partial<MeetingVenue>) =>
    setForm((f) => ({ ...f, meetings: f.meetings.map((m, i) => (i === idx ? { ...m, ...patch } : m)) }));
  const removeMeeting = (idx: number) =>
    setForm((f) => ({ ...f, meetings: f.meetings.filter((_, i) => i !== idx) }));

  const addDining = () =>
    setForm((f) => ({
      ...f,
      dining: [...f.dining, { id: uid('din'), name: '', address: '', scheduledAt: '', notes: '' }],
    }));
  const updateDining = (idx: number, patch: Partial<DiningReservation>) =>
    setForm((f) => ({ ...f, dining: f.dining.map((d, i) => (i === idx ? { ...d, ...patch } : d)) }));
  const removeDining = (idx: number) =>
    setForm((f) => ({ ...f, dining: f.dining.filter((_, i) => i !== idx) }));

  const handleSubmit = async () => {
    if (!isFormValid(form)) {
      setError('Please fill all required fields (protected person, title, dates, and at least one itinerary leg).');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      // `create` now throws the server's own message (e.g. the gateway's
      // "Too many requests…") instead of returning null, so the catch below
      // surfaces the real reason rather than a generic "Failed to generate".
      const created = await tripBriefsService.create(formToCreateInput(form));
      onCreated(created);
      setForm(emptyForm());
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate trip brief');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-12">
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl dark:bg-secondary-900">
        <div className="flex items-center justify-between border-b border-secondary-200 px-6 py-4 dark:border-secondary-700">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">New Travel Risk Brief</h2>
            <p className="text-sm text-secondary-500 dark:text-secondary-400">
              Generation auto-starts when the required fields are valid.
            </p>
          </div>
          <button onClick={onClose} className="text-secondary-500 hover:text-secondary-900 dark:hover:text-white">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-secondary-900 dark:text-secondary-100">
                Protected Person <span className="text-danger-500">*</span>
              </label>
              <select
                value={form.protectedPersonId}
                onChange={(e) => {
                  const personId = e.target.value;
                  setField('protectedPersonId', personId);
                  const person = protectedPersons.find((p) => p.id === personId);
                  if (person) {
                    const conditions = person.medicalConditions?.length
                      ? person.medicalConditions.join(', ')
                      : '';
                    setForm((f) => ({
                      ...f,
                      protectedPersonId: personId,
                      medicalConditions: conditions || f.medicalConditions,
                      additionalNotes: person.specialRequirements
                        ? (f.additionalNotes ? `${f.additionalNotes}\n${person.specialRequirements}` : person.specialRequirements)
                        : f.additionalNotes,
                    }));
                  }
                }}
                className="h-10 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
              >
                <option value="">— Select —</option>
                {protectedPersons.map((p) => (
                  <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
                ))}
              </select>
            </div>
            <Input
              label="Trip Title"
              required
              placeholder="e.g. Q3 Tokyo Investor Tour"
              value={form.title}
              onChange={(e) => setField('title', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-secondary-900 dark:text-secondary-100">Purpose</label>
              <select
                value={form.purpose}
                onChange={(e) => setField('purpose', e.target.value as TripPurpose)}
                className="h-10 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
              >
                {PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-secondary-900 dark:text-secondary-100">Classification</label>
              <select
                value={form.classification}
                onChange={(e) => setField('classification', e.target.value as TripClassification)}
                className="h-10 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
              >
                {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <Input
              label="Requesting Party"
              placeholder="e.g. CFO Office"
              value={form.requestingParty}
              onChange={(e) => setField('requestingParty', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 gap-4">
            <div>
              <Input
                label="Executive Industry"
                placeholder="e.g. Health Insurance, Pharmaceuticals, Energy"
                value={form.executiveIndustry}
                onChange={(e) => setField('executiveIndustry', e.target.value)}
              />
              <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                Drives the profile/industry risk floor — high-grievance sectors (e.g. health insurance) elevate the overall rating regardless of destination.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <DatePicker
              label="Depart Date"
              required
              value={form.departDate}
              onChange={(v) => setField('departDate', v)}
            />
            <DatePicker
              label="Return Date"
              required
              value={form.returnDate}
              onChange={(v) => setField('returnDate', v)}
            />
            <DatePicker
              label="Brief Needed By"
              value={form.briefNeededByDate}
              onChange={(v) => setField('briefNeededByDate', v)}
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                Itinerary <span className="text-danger-500">*</span>
              </h3>
              <Button type="button" variant="ghost" size="sm" onClick={addLeg}>
                <PlusIcon className="mr-1 h-4 w-4" /> Add Leg
              </Button>
            </div>
            <div className="space-y-3">
              {form.itinerary.map((leg, idx) => (
                <div key={leg.id} className="grid grid-cols-1 gap-2 rounded-lg border border-secondary-200 p-3 dark:border-secondary-700 md:grid-cols-12">
                  <div className="md:col-span-3 space-y-2">
                    <Input
                      placeholder="City"
                      value={leg.city}
                      onChange={(e) => updateLeg(idx, { city: e.target.value })}
                    />
                    {countryHasSubdivisions(leg.country) && (
                      <Input
                        aria-label="State or province"
                        placeholder="State / Province"
                        value={leg.region ?? ''}
                        onChange={(e) => updateLeg(idx, { region: e.target.value })}
                      />
                    )}
                  </div>
                  <div className="md:col-span-3">
                    <CountryCombobox
                      ariaLabel="Country"
                      placeholder="Country"
                      value={leg.country}
                      onChange={(v) =>
                        updateLeg(idx, countryHasSubdivisions(v) ? { country: v } : { country: v, region: undefined })
                      }
                    />
                  </div>
                  <div className="md:col-span-2">
                    <DatePicker
                      ariaLabel="Leg start date"
                      placeholder="Start date"
                      value={leg.startDate}
                      onChange={(v) => updateLeg(idx, { startDate: v })}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <DatePicker
                      ariaLabel="Leg end date"
                      placeholder="End date"
                      value={leg.endDate}
                      onChange={(v) => updateLeg(idx, { endDate: v })}
                    />
                  </div>
                  <div className="flex items-center justify-end md:col-span-2">
                    {form.itinerary.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeLeg(idx)}>
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Logistics (collapsible) ── */}
          <div>
            <button
              type="button"
              className="mb-2 flex w-full items-center justify-between text-sm font-semibold text-secondary-900 dark:text-secondary-100"
              onClick={() => setShowLogistics((v) => !v)}
            >
              <span>Logistics (airports, flights, transport, accommodation, venues)</span>
              <span className="text-xs text-secondary-500">{showLogistics ? 'Hide' : 'Show'}</span>
            </button>

            {showLogistics && (
              <div className="space-y-4 rounded-lg border border-secondary-200 p-4 dark:border-secondary-700">
                {/* Airports */}
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Input
                    label="Departure Airport"
                    placeholder="e.g. JFK"
                    value={form.departureAirport}
                    onChange={(e) => setField('departureAirport', e.target.value)}
                  />
                  <Input
                    label="Arrival Airport"
                    placeholder="e.g. NRT"
                    value={form.arrivalAirport}
                    onChange={(e) => setField('arrivalAirport', e.target.value)}
                  />
                </div>

                {/* Flights */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wide text-secondary-600 dark:text-secondary-300">Flights</span>
                    <Button type="button" variant="ghost" size="sm" onClick={addFlight}>
                      <PlusIcon className="mr-1 h-3 w-3" /> Add Flight
                    </Button>
                  </div>
                  {form.flights.map((fl, idx) => (
                    <div key={fl.id} className="mb-2 grid grid-cols-1 gap-2 rounded-lg border border-secondary-100 p-2 dark:border-secondary-700 md:grid-cols-12">
                      <div className="md:col-span-2">
                        <Input placeholder="Carrier" value={fl.carrier} onChange={(e) => updateFlight(idx, { carrier: e.target.value })} />
                      </div>
                      <div className="md:col-span-2">
                        <Input placeholder="Flight #" value={fl.flightNumber} onChange={(e) => updateFlight(idx, { flightNumber: e.target.value })} />
                      </div>
                      <div className="md:col-span-2">
                        <Input placeholder="From (IATA)" value={fl.departureAirport} onChange={(e) => updateFlight(idx, { departureAirport: e.target.value })} />
                      </div>
                      <div className="md:col-span-2">
                        <Input placeholder="To (IATA)" value={fl.arrivalAirport} onChange={(e) => updateFlight(idx, { arrivalAirport: e.target.value })} />
                      </div>
                      <div className="md:col-span-2">
                        <Input type="datetime-local" placeholder="Departs" value={fl.departureTime} onChange={(e) => updateFlight(idx, { departureTime: e.target.value })} />
                      </div>
                      <div className="flex items-center gap-1 md:col-span-2">
                        <Input type="datetime-local" placeholder="Arrives" value={fl.arrivalTime} onChange={(e) => updateFlight(idx, { arrivalTime: e.target.value })} />
                        <button type="button" onClick={() => removeFlight(idx)} className="text-secondary-400 hover:text-danger-500">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Ground Transport */}
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-secondary-900 dark:text-secondary-100">Ground Transport</label>
                    <select
                      value={form.groundTransportMode}
                      onChange={(e) => setField('groundTransportMode', e.target.value as GroundTransportMode | '')}
                      className="h-10 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
                    >
                      <option value="">— Select —</option>
                      {GROUND_TRANSPORT_MODES.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  <Input
                    label="Transport Notes"
                    placeholder="e.g. Pre-booked armored vehicle"
                    value={form.groundTransportNotes}
                    onChange={(e) => setField('groundTransportNotes', e.target.value)}
                  />
                </div>

                {/* Accommodations */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wide text-secondary-600 dark:text-secondary-300">Accommodations</span>
                    <Button type="button" variant="ghost" size="sm" onClick={addAccommodation}>
                      <PlusIcon className="mr-1 h-3 w-3" /> Add
                    </Button>
                  </div>
                  {form.accommodations.map((acc, idx) => (
                    <div key={acc.id} className="mb-2 grid grid-cols-1 gap-2 rounded-lg border border-secondary-100 p-2 dark:border-secondary-700 md:grid-cols-12">
                      <div className="md:col-span-3">
                        <Input placeholder="Hotel Name" value={acc.name} onChange={(e) => updateAccommodation(idx, { name: e.target.value })} />
                      </div>
                      <div className="md:col-span-3">
                        <Input placeholder="Address" value={acc.address} onChange={(e) => updateAccommodation(idx, { address: e.target.value })} />
                      </div>
                      <div className="md:col-span-2">
                        <DatePicker ariaLabel="Check-in" placeholder="Check-in" value={acc.checkIn} onChange={(v) => updateAccommodation(idx, { checkIn: v })} />
                      </div>
                      <div className="md:col-span-2">
                        <DatePicker ariaLabel="Check-out" placeholder="Check-out" value={acc.checkOut} onChange={(v) => updateAccommodation(idx, { checkOut: v })} />
                      </div>
                      <div className="flex items-center gap-2 md:col-span-2">
                        <label className="flex items-center gap-1 text-xs text-secondary-600 dark:text-secondary-300">
                          <input type="checkbox" checked={acc.aliasBooking ?? false} onChange={(e) => updateAccommodation(idx, { aliasBooking: e.target.checked })} />
                          Alias
                        </label>
                        <button type="button" onClick={() => removeAccommodation(idx)} className="text-secondary-400 hover:text-danger-500">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Meetings / Venues */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wide text-secondary-600 dark:text-secondary-300">Meetings / Venues</span>
                    <Button type="button" variant="ghost" size="sm" onClick={addMeeting}>
                      <PlusIcon className="mr-1 h-3 w-3" /> Add
                    </Button>
                  </div>
                  {form.meetings.map((mtg, idx) => (
                    <div key={mtg.id} className="mb-2 grid grid-cols-1 gap-2 rounded-lg border border-secondary-100 p-2 dark:border-secondary-700 md:grid-cols-12">
                      <div className="md:col-span-3">
                        <Input placeholder="Venue Name" value={mtg.name} onChange={(e) => updateMeeting(idx, { name: e.target.value })} />
                      </div>
                      <div className="md:col-span-3">
                        <Input placeholder="Address" value={mtg.address} onChange={(e) => updateMeeting(idx, { address: e.target.value })} />
                      </div>
                      <div className="md:col-span-3">
                        <Input type="datetime-local" placeholder="Scheduled" value={mtg.scheduledAt} onChange={(e) => updateMeeting(idx, { scheduledAt: e.target.value })} />
                      </div>
                      <div className="flex items-center gap-1 md:col-span-3">
                        <Input placeholder="Notes" value={mtg.notes ?? ''} onChange={(e) => updateMeeting(idx, { notes: e.target.value })} />
                        <button type="button" onClick={() => removeMeeting(idx)} className="text-secondary-400 hover:text-danger-500">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Dining */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wide text-secondary-600 dark:text-secondary-300">Dining</span>
                    <Button type="button" variant="ghost" size="sm" onClick={addDining}>
                      <PlusIcon className="mr-1 h-3 w-3" /> Add
                    </Button>
                  </div>
                  {form.dining.map((din, idx) => (
                    <div key={din.id} className="mb-2 grid grid-cols-1 gap-2 rounded-lg border border-secondary-100 p-2 dark:border-secondary-700 md:grid-cols-12">
                      <div className="md:col-span-3">
                        <Input placeholder="Restaurant" value={din.name} onChange={(e) => updateDining(idx, { name: e.target.value })} />
                      </div>
                      <div className="md:col-span-3">
                        <Input placeholder="Address" value={din.address} onChange={(e) => updateDining(idx, { address: e.target.value })} />
                      </div>
                      <div className="md:col-span-3">
                        <Input type="datetime-local" placeholder="Reservation" value={din.scheduledAt} onChange={(e) => updateDining(idx, { scheduledAt: e.target.value })} />
                      </div>
                      <div className="flex items-center gap-1 md:col-span-3">
                        <Input placeholder="Notes" value={din.notes ?? ''} onChange={(e) => updateDining(idx, { notes: e.target.value })} />
                        <button type="button" onClick={() => removeDining(idx)} className="text-secondary-400 hover:text-danger-500">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-secondary-900 dark:text-secondary-100">Special Considerations</h3>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <label className="flex items-center gap-2 text-sm text-secondary-700 dark:text-secondary-200">
                <input type="checkbox" checked={form.lgbtqTraveler} onChange={(e) => setField('lgbtqTraveler', e.target.checked)} />
                LGBTQ+ traveler
              </label>
              <label className="flex items-center gap-2 text-sm text-secondary-700 dark:text-secondary-200">
                <input type="checkbox" checked={form.soloFemaleTraveler} onChange={(e) => setField('soloFemaleTraveler', e.target.checked)} />
                Solo female traveler
              </label>
              <label className="flex items-center gap-2 text-sm text-secondary-700 dark:text-secondary-200">
                <input type="checkbox" checked={form.publicFacingProfile} onChange={(e) => setField('publicFacingProfile', e.target.checked)} />
                Public-facing profile
              </label>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-secondary-900 dark:text-secondary-100">Security Detail</label>
                <select
                  value={form.securityDetail}
                  onChange={(e) => setField('securityDetail', e.target.value as 'NONE' | 'COVERT' | 'OVERT')}
                  className="h-10 w-full rounded-lg border border-secondary-300 bg-white px-3 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
                >
                  <option value="NONE">None</option>
                  <option value="COVERT">Covert</option>
                  <option value="OVERT">Overt</option>
                </select>
              </div>
              <Input
                label="Dietary Requirements"
                placeholder="Optional"
                value={form.dietaryRequirements}
                onChange={(e) => setField('dietaryRequirements', e.target.value)}
              />
              <Input
                label="Medical Conditions"
                placeholder="Optional"
                value={form.medicalConditions}
                onChange={(e) => setField('medicalConditions', e.target.value)}
              />
              <Input
                label="Disability Accommodations"
                placeholder="Optional"
                value={form.disabilityAccommodations}
                onChange={(e) => setField('disabilityAccommodations', e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-secondary-900 dark:text-secondary-100">Traveler / Analyst Notes</label>
            <textarea
              rows={3}
              value={form.travelerNotes}
              onChange={(e) => setField('travelerNotes', e.target.value)}
              className="w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
              placeholder="Anything additional to feed the brief generator…"
            />
          </div>

          {error && (
            <div className="rounded-lg border border-danger-300 bg-danger-50 px-3 py-2 text-sm text-danger-700 dark:border-danger-700 dark:bg-danger-900/20 dark:text-danger-300">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-secondary-200 px-6 py-4 dark:border-secondary-700">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} loading={submitting} disabled={!isFormValid(form) || submitting}>
            Generate Brief
          </Button>
        </div>
      </div>
    </div>
  );
}

interface BriefDetailDrawerProps {
  brief: TripBriefRecord;
  onClose: () => void;
  onChanged: (b: TripBriefRecord) => void;
}

function BriefDetailDrawer({ brief, onClose, onChanged }: BriefDetailDrawerProps) {
  const queryClient = useQueryClient();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  const [editSummary, setEditSummary] = useState('');
  const [approverName, setApproverName] = useState('');
  const [approverTitle, setApproverTitle] = useState('');
  const [showApprove, setShowApprove] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState('');
  const [showDeliver, setShowDeliver] = useState(false);
  const [deliverEmail, setDeliverEmail] = useState('');

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['trip-briefs'] });

  // While the AI summary is still generating, poll the brief until it resolves.
  const liveDataPending = brief.liveDataStatus === 'pending' && !(Date.now() - new Date(brief.updatedAt).getTime() > AI_SUMMARY_STUCK_MS);
  const shouldPoll = (brief.aiSummaryStatus === 'PENDING' || liveDataPending) && !isAiSummaryStuck(brief);
  const { data: polledBrief, error: pollError } = useQuery({
    queryKey: ['trip-brief', brief.id],
    queryFn: () => tripBriefsService.getById(brief.id),
    enabled: shouldPoll,
    // 15s, not 3s. AI sourcing runs for tens of minutes (14 sections, 90s
    // timeout, 3 retries each), and at 3s this single view was ~300 requests per
    // 15-minute window — about two thirds of all the app's background traffic,
    // against the gateway's 1000 req/15min per-user cap in production. Sections
    // land ~60-90s apart, so a 3s poll was resolving nothing 95% of the time.
    refetchInterval: shouldPoll ? 15_000 : false,
    // getById now throws on failure, so cap retries: react-query's default of 3
    // would quadruple the request cost of every failed tick and help exhaust the
    // very rate limit that caused the failure.
    retry: 1,
  });
  useEffect(() => {
    // Propagate on ANY server-side change, not just the top-level status enums.
    // During sourcing `liveDataStatus` stays 'pending' the whole time while the
    // per-section `dataStatus` flips incrementally — each merge bumps `updatedAt`.
    // Gating only on the enums left the "N/16 sourced" counter frozen until a
    // manual refresh; `updatedAt` catches every incremental section completion
    // (and the live-weather rescore).
    if (polledBrief && briefLiveProgressChanged(brief, polledBrief)) {
      onChanged(polledBrief);
    }
  }, [polledBrief, onChanged, brief]);

  const startEdit = (key: string, body: string, summary: string) => {
    setEditingKey(key);
    setEditBody(body);
    setEditSummary(summary);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditBody('');
    setEditSummary('');
  };

  const saveEdit = async () => {
    if (!editingKey) {return;}
    setBusyKey(editingKey);
    try {
      const sections = brief.sections.map((s) =>
        s.key === editingKey ? { ...s, body: editBody, summary: editSummary, edited: true } : s,
      );
      const updated = await tripBriefsService.update(brief.id, { sections });
      if (updated) {
        onChanged(updated);
        invalidate();
      }
      cancelEdit();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setBusyKey(null);
    }
  };

  const saveSummary = async () => {
    setBusyKey('ai-summary');
    try {
      const updated = await tripBriefsService.update(brief.id, { flashSummary: summaryDraft });
      if (updated) {
        onChanged(updated);
        invalidate();
      }
      setEditingSummary(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save summary');
    } finally {
      setBusyKey(null);
    }
  };

  const regenerate = async (key: string) => {
    setBusyKey(key);
    try {
      const updated = await tripBriefsService.regenerateSection(brief.id, key);
      if (updated) {
        onChanged(updated);
        invalidate();
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to regenerate section');
    } finally {
      setBusyKey(null);
    }
  };

  const flag = async (key: string, needs: boolean) => {
    setBusyKey(`${key}-flag`);
    try {
      const updated = await tripBriefsService.flagSection(brief.id, key, needs);
      if (updated) {
        onChanged(updated);
        invalidate();
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update flag');
    } finally {
      setBusyKey(null);
    }
  };

  const refresh = async () => {
    setBusyKey('refresh');
    try {
      const updated = await tripBriefsService.refresh(brief.id);
      if (updated) {
        onChanged(updated);
        invalidate();
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to refresh brief');
    } finally {
      setBusyKey(null);
    }
  };

  const rerunReview = async () => {
    setBusyKey('rerun');
    try {
      const updated = await tripBriefsService.rerun(brief.id);
      if (updated) { onChanged(updated); invalidate(); }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to re-run review');
    } finally {
      setBusyKey(null);
    }
  };

  const stopReview = async () => {
    setBusyKey('cancel');
    try {
      const updated = await tripBriefsService.cancel(brief.id);
      if (updated) { onChanged(updated); invalidate(); }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel review');
    } finally {
      setBusyKey(null);
    }
  };

  const approve = async () => {
    if (!approverName.trim()) {return;}
    setBusyKey('approve');
    try {
      const updated = await tripBriefsService.approve(brief.id, approverName.trim(), approverTitle.trim() || undefined);
      if (updated) {
        onChanged(updated);
        invalidate();
        setShowApprove(false);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve brief');
    } finally {
      setBusyKey(null);
    }
  };

  const exportPdf = async () => {
    setBusyKey('pdf');
    try {
      await tripBriefsService.downloadPdf(brief.id, `${brief.briefNumber}.pdf`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'PDF download failed');
    } finally {
      setBusyKey(null);
    }
  };

  const exportDocx = async () => {
    setBusyKey('docx');
    try {
      await tripBriefsService.downloadDocx(brief.id, `${brief.briefNumber}.docx`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'DOCX download failed');
    } finally {
      setBusyKey(null);
    }
  };

  const deliverBrief = async () => {
    if (!deliverEmail.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setBusyKey('deliver');
    try {
      await tripBriefsService.deliver(brief.id, deliverEmail.trim());
      toast.success(`Brief delivered to ${deliverEmail}`);
      setShowDeliver(false);
      setDeliverEmail('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Delivery failed');
    } finally {
      setBusyKey(null);
    }
  };

  const locked = brief.status === 'APPROVED';

  const liveTotal = brief.sections.length + 1; // 15 sections + executive summary
  const liveDone =
    brief.sections.filter((s) => s.dataStatus && s.dataStatus !== 'pending').length +
    (brief.aiSummaryStatus !== 'PENDING' ? 1 : 0);
  const liveStale = Date.now() - new Date(brief.updatedAt).getTime() > AI_SUMMARY_STUCK_MS;
  const liveGenerating =
    (brief.liveDataStatus === 'pending' || brief.aiSummaryStatus === 'PENDING') && !liveStale;

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40">
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="h-full w-full max-w-4xl overflow-y-auto bg-white shadow-2xl dark:bg-secondary-900"
      >
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-secondary-200 bg-white px-6 py-4 dark:border-secondary-700 dark:bg-secondary-900">
          <div>
            <div className="flex items-center gap-2">
              <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', liveGenerating ? STATUS_BADGES.GENERATING : STATUS_BADGES[brief.status])}>
                {liveGenerating ? 'RUNNING' : brief.status}
              </span>
              <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200">
                v{brief.version}
              </span>
              <span className="font-mono text-xs text-secondary-500 dark:text-secondary-400">{brief.briefNumber}</span>
            </div>
            <h2 className="mt-1 text-xl font-semibold text-secondary-900 dark:text-white">{brief.title}</h2>
            <p className="text-sm text-secondary-500 dark:text-secondary-400">
              {brief.protectedPersonName} · {formatDate(brief.departDate)} → {formatDate(brief.returnDate)} · {brief.purpose}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!locked && (
              <Button variant="outline" size="sm" leftIcon={<ArrowPathIcon className="h-4 w-4" />} loading={busyKey === 'rerun'} onClick={rerunReview}>
                Re-run review
              </Button>
            )}
            <Button variant="outline" size="sm" leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />} loading={busyKey === 'pdf'} onClick={exportPdf}>
              PDF
            </Button>
            <Button variant="outline" size="sm" leftIcon={<ArrowDownTrayIcon className="h-4 w-4" />} loading={busyKey === 'docx'} onClick={exportDocx}>
              DOCX
            </Button>
            <Button variant="outline" size="sm" leftIcon={<EnvelopeIcon className="h-4 w-4" />} loading={busyKey === 'deliver'} onClick={() => setShowDeliver(true)}>
              Deliver
            </Button>
            {locked ? (
              <Button variant="outline" size="sm" leftIcon={<ArrowPathIcon className="h-4 w-4" />} loading={busyKey === 'refresh'} onClick={refresh}>
                Refresh
              </Button>
            ) : (
              <Button size="sm" leftIcon={<CheckBadgeIcon className="h-4 w-4" />} onClick={() => setShowApprove(true)}>
                Approve
              </Button>
            )}
            <button onClick={onClose} className="text-secondary-500 hover:text-secondary-900 dark:hover:text-white">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="space-y-4 px-6 py-5">
          {liveGenerating && (
            <div className="rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-3 dark:border-secondary-700 dark:bg-secondary-800/50">
              <div className="mb-1.5 flex items-center justify-between text-xs font-medium text-secondary-700 dark:text-secondary-200">
                <span className="inline-flex items-center gap-1.5">
                  <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> Sourcing live data…
                </span>
                <div className="flex items-center gap-2">
                  <span>{liveDone}/{liveTotal} sourced</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<NoSymbolIcon className="h-3.5 w-3.5" />}
                    loading={busyKey === 'cancel'}
                    onClick={stopReview}
                  >
                    Stop
                  </Button>
                </div>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-secondary-200 dark:bg-secondary-700">
                <div
                  className="h-full rounded-full bg-primary-600 transition-all duration-500"
                  style={{ width: `${Math.round((liveDone / liveTotal) * 100)}%` }}
                />
              </div>
              {/* A failing poll used to be invisible: getById returned null, the
                  propagation guard skipped it, and the counter simply stopped
                  advancing — which read as "the scan hung". Say so instead. */}
              {pollError && (
                <p className="mt-2 text-xs text-warning-700 dark:text-warning-400">
                  Can’t reach the server for progress updates ({pollError instanceof Error ? pollError.message : 'unknown error'}).
                  Generation is still running server-side — this will resume on its own.
                </p>
              )}
            </div>
          )}
          {brief.flashCover && <FlashCover cover={brief.flashCover} />}

          <Card>
            <CardContent className="space-y-2 p-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                  Executive Summary
                  {brief.aiSummaryStatus === 'READY' && (
                    <span className="ml-2 rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-200">
                      AI-generated · verify before action
                    </span>
                  )}
                </h3>
                {!locked && brief.aiSummaryStatus !== 'PENDING' && !editingSummary && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSummaryDraft(brief.flashSummary);
                      setEditingSummary(true);
                    }}
                  >
                    <PencilSquareIcon className="mr-1 h-4 w-4" /> Edit
                  </Button>
                )}
              </div>

              {brief.aiRecommendation && (
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className={cn('rounded-full px-2.5 py-1 text-xs font-bold', REC_BADGES[brief.aiRecommendation] ?? 'bg-secondary-100 text-secondary-700')}>
                    {brief.aiRecommendation.replace(/_/g, ' ')}
                  </span>
                  {brief.aiRecommendationRationale && (
                    <span className="text-xs text-secondary-600 dark:text-secondary-300">{brief.aiRecommendationRationale}</span>
                  )}
                </div>
              )}

              {brief.aiSummaryStatus === 'PENDING' && !isAiSummaryStuck(brief) ? (
                <div className="space-y-2" aria-live="polite">
                  <div className="flex items-center gap-2 text-sm text-secondary-500 dark:text-secondary-400">
                    <ArrowPathIcon className="h-4 w-4 animate-spin" /> Generating live AI summary…
                  </div>
                  <div className="h-3 w-3/4 animate-pulse rounded bg-secondary-200 dark:bg-secondary-700" />
                  <div className="h-3 w-2/3 animate-pulse rounded bg-secondary-200 dark:bg-secondary-700" />
                </div>
              ) : editingSummary ? (
                <div className="space-y-2">
                  <textarea
                    rows={6}
                    value={summaryDraft}
                    onChange={(e) => setSummaryDraft(e.target.value)}
                    className="w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
                  />
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditingSummary(false)}>Cancel</Button>
                    <Button size="sm" loading={busyKey === 'ai-summary'} onClick={saveSummary}>Save</Button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-secondary-800 dark:text-secondary-100">
                    {brief.flashSummary.replace(/\*\*/g, '')}
                  </p>
                  {(brief.aiSummaryStatus === 'FAILED' || isAiSummaryStuck(brief)) && (
                    <p className="text-xs text-secondary-400 dark:text-secondary-500">
                      Live AI summary unavailable — showing the standard generated summary.
                    </p>
                  )}
                  {brief.aiSummaryStatus === 'READY' && brief.aiSummarySources.length > 0 && (
                    <div className="pt-1 text-xs text-secondary-500 dark:text-secondary-400">
                      <span className="font-medium">Sources:</span>{' '}
                      {brief.aiSummarySources.map((s, i) => (
                        <span key={s.url}>
                          {i > 0 && ' · '}
                          <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline dark:text-primary-400">
                            {s.label}
                          </a>
                        </span>
                      ))}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {brief.sections.map((section) => {
            const isEditing = editingKey === section.key;
            const sectionBusy = busyKey === section.key;
            return (
              <Card key={section.key}>
                <CardContent className="space-y-3 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-base font-semibold text-secondary-900 dark:text-white">{section.title}</span>
                      {section.dataStatus === 'live' && (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200">
                          Live{section.dataSource ? ` · ${section.dataSource}` : ''}
                        </span>
                      )}
                      {section.dataStatus === 'pending' && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-medium text-secondary-600 dark:bg-secondary-800 dark:text-secondary-300">
                          <ArrowPathIcon className="h-3 w-3 animate-spin" /> updating live data…
                        </span>
                      )}
                      {section.riskRating && (
                        <span className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', RISK_COLORS[section.riskRating])}>
                          {section.riskRating}
                        </span>
                      )}
                      {section.needsReview && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-200">
                          <ExclamationTriangleIcon className="h-3 w-3" /> needs review
                        </span>
                      )}
                      {section.edited && (
                        <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200">
                          edited
                        </span>
                      )}
                    </div>
                    {!locked && !isEditing && (
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => startEdit(section.key, section.body, section.summary)}>
                          <PencilSquareIcon className="mr-1 h-4 w-4" /> Edit
                        </Button>
                        <Button variant="ghost" size="sm" loading={sectionBusy} onClick={() => regenerate(section.key)}>
                          <ArrowPathIcon className="mr-1 h-4 w-4" /> Regenerate
                        </Button>
                        <Button variant="ghost" size="sm" loading={busyKey === `${section.key}-flag`} onClick={() => flag(section.key, !section.needsReview)}>
                          <ExclamationTriangleIcon className="mr-1 h-4 w-4" />
                          {section.needsReview ? 'Clear flag' : 'Flag'}
                        </Button>
                      </div>
                    )}
                  </div>
                  {isEditing ? (
                    <div className="space-y-2">
                      <textarea
                        rows={2}
                        value={editSummary}
                        onChange={(e) => setEditSummary(e.target.value)}
                        className="w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm italic dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
                        placeholder="Summary"
                      />
                      <textarea
                        rows={6}
                        value={editBody}
                        onChange={(e) => setEditBody(e.target.value)}
                        className="w-full rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
                        placeholder="Section body (markdown supported)"
                      />
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={cancelEdit}>Cancel</Button>
                        <Button size="sm" loading={sectionBusy} onClick={saveEdit}>Save</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm italic text-secondary-600 dark:text-secondary-300">{section.summary}</p>
                      <SectionBody body={section.body} />
                      {section.bullets && section.bullets.length > 0 && (
                        <ul className="ml-5 list-disc text-sm text-secondary-800 dark:text-secondary-100">
                          {section.bullets.map((b, i) => <li key={i}>{b}</li>)}
                        </ul>
                      )}
                    </>
                  )}
                  <div className="text-xs text-secondary-500 dark:text-secondary-400">
                    Sources: {section.sources.map((s) => s.label).join(' · ')} · Freshness: {section.freshnessTimestamp.slice(0, 16).replace('T', ' ')} UTC
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {brief.medicalFacilities.length > 0 && (
            <Card>
              <CardContent className="space-y-2 p-4">
                <h3 className="text-base font-semibold text-secondary-900 dark:text-white">Appendix A — Medical Facilities</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wide text-secondary-500">
                      <tr><th className="py-2 pr-4">Facility</th><th className="py-2 pr-4">Type</th><th className="py-2 pr-4">Phone</th><th className="py-2">Capabilities</th></tr>
                    </thead>
                    <tbody>
                      {brief.medicalFacilities.map((f, i) => (
                        <tr key={`${f.name}-${i}`} className="border-t border-secondary-100 dark:border-secondary-700">
                          <td className="py-2 pr-4 font-medium text-secondary-900 dark:text-white">{f.name}<div className="text-xs text-secondary-500">{f.address}</div></td>
                          <td className="py-2 pr-4">{f.type.replace(/_/g, ' ')}</td>
                          <td className="py-2 pr-4">{f.phone ?? '—'}</td>
                          <td className="py-2">{f.capabilities.join(', ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {brief.travelerTips.length > 0 && (
            <Card>
              <CardContent className="space-y-3 p-4">
                <h3 className="text-base font-semibold text-secondary-900 dark:text-white">Appendix B — Traveler Tips</h3>
                {brief.travelerTips.map((t, i) => (
                  <div key={i} className="rounded-lg border border-secondary-200 p-3 dark:border-secondary-700">
                    <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                      {t.category} — {t.title}
                    </div>
                    <p className="mt-1 text-sm text-secondary-800 dark:text-secondary-100">{t.body}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {brief.approvedByName && (
            <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-200">
              Approved by <strong>{brief.approvedByName}</strong>{brief.approvedByTitle ? ` (${brief.approvedByTitle})` : ''} on {formatDate(brief.approvedAt)} — ISO 31030 audit logged.
            </div>
          )}
        </div>

        {showApprove && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl dark:bg-secondary-900">
              <h3 className="text-base font-semibold text-secondary-900 dark:text-white">Approve Travel Risk Brief</h3>
              <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                Approval locks the brief, stamps your name, and writes an ISO 31030 audit entry.
              </p>
              <div className="mt-4 space-y-3">
                <Input label="Approver Name" required value={approverName} onChange={(e) => setApproverName(e.target.value)} />
                <Input label="Approver Title" value={approverTitle} onChange={(e) => setApproverTitle(e.target.value)} placeholder="e.g. Director, Corporate Security" />
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowApprove(false)}>Cancel</Button>
                <Button loading={busyKey === 'approve'} onClick={approve} disabled={!approverName.trim()}>Confirm Approve</Button>
              </div>
            </div>
          </div>
        )}

        {showDeliver && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl dark:bg-secondary-900">
              <h3 className="text-base font-semibold text-secondary-900 dark:text-white">Deliver Brief by Email</h3>
              <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                Sends a formatted email with the executive summary and section overview.
              </p>
              <div className="mt-4">
                <Input
                  label="Recipient Email"
                  required
                  type="email"
                  placeholder="e.g. exec@company.com"
                  value={deliverEmail}
                  onChange={(e) => setDeliverEmail(e.target.value)}
                />
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setShowDeliver(false); setDeliverEmail(''); }}>Cancel</Button>
                <Button
                  loading={busyKey === 'deliver'}
                  onClick={deliverBrief}
                  disabled={!deliverEmail.includes('@')}
                  leftIcon={<EnvelopeIcon className="h-4 w-4" />}
                >
                  Send
                </Button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

/**
 * Fetches the full brief for the drawer.
 *
 * The list endpoint returns `TripBriefSummary` (no section bodies, no flash cover),
 * so a card click no longer has a complete record to hand over — it passes an id
 * and this loads the rest. Kept as a wrapper so `BriefDetailDrawer` keeps owning
 * its own local copy via `onChanged`, exactly as before.
 *
 * `retry: 0` on purpose: if a single brief's read hangs, the axios timeout surfaces
 * one error here rather than three sequential 30s waits, and it stays contained to
 * this drawer instead of blanking the list behind it.
 */
function BriefDetailLoader({ briefId, onClose }: { briefId: string; onClose: () => void }) {
  const [brief, setBrief] = useState<TripBriefRecord | null>(null);
  const { data, isError, error } = useQuery({
    queryKey: ['trip-brief', briefId, 'full'],
    queryFn: () => tripBriefsService.getById(briefId),
    enabled: brief === null,
    retry: 0,
  });

  useEffect(() => {
    if (data && brief === null) { setBrief(data); }
  }, [data, brief]);

  if (brief) {
    return <BriefDetailDrawer brief={brief} onClose={onClose} onChanged={setBrief} />;
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/40">
      <div className="flex h-full w-full max-w-4xl flex-col items-center justify-center gap-3 bg-white px-6 text-center dark:bg-secondary-900">
        {isError ? (
          <>
            <ExclamationTriangleIcon className="h-10 w-10 text-danger-500" />
            <h3 className="text-base font-semibold text-secondary-900 dark:text-white">Couldn’t open this brief</h3>
            <p className="max-w-md text-sm text-secondary-500 dark:text-secondary-400">
              {error instanceof Error ? error.message : 'The executive-protection service did not respond.'}
            </p>
            <p className="max-w-md text-xs text-secondary-500 dark:text-secondary-400">
              The brief still exists — this is a read failure. Other briefs are unaffected.
            </p>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </>
        ) : (
          <p className="text-sm text-secondary-500 dark:text-secondary-400">Loading brief…</p>
        )}
      </div>
    </div>
  );
}

export default function TripPlanner() {
  const { protectedPersons } = useOutletContext<ExecutiveProtectionContextType>();
  const queryClient = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [search, setSearch] = useState('');
  const [activeBriefId, setActiveBriefId] = useState<string | null>(null);

  const { data: briefs = [], isLoading, isError, error, refetch } = useQuery({
    queryKey: ['trip-briefs', statusFilter, search],
    queryFn: () => tripBriefsService.list({
      status: statusFilter === 'ALL' ? undefined : statusFilter,
      search: search.trim() || undefined,
    }),
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: 1,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => tripBriefsService.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trip-briefs'] }),
    // Surface failures (e.g. a 403 from the write-role gate) instead of silently
    // leaving the card in place. (L-004: every mutation catches + surfaces.)
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to delete brief'),
  });

  const personOptions = useMemo(
    () => protectedPersons.map((p) => ({
      id: p.id,
      firstName: p.firstName,
      lastName: p.lastName,
      medicalConditions: p.medicalConditions,
      specialRequirements: p.specialRequirements,
    })),
    [protectedPersons],
  );

  const handleCreated = (brief: TripBriefRecord) => {
    queryClient.invalidateQueries({ queryKey: ['trip-briefs'] });
    setActiveBriefId(brief.id);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="Search briefs…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="md:w-64"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 rounded-lg border border-secondary-300 bg-white px-3 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
          >
            <option value="ALL">All statuses</option>
            <option value="READY">Ready</option>
            <option value="APPROVED">Approved</option>
            <option value="DRAFT">Draft</option>
            <option value="ARCHIVED">Archived</option>
          </select>
        </div>
        <Button onClick={() => setShowNew(true)} leftIcon={<PlusIcon className="h-4 w-4" />}>
          New Travel Risk Brief
        </Button>
      </div>

      {isLoading ? (
        <div className="rounded-xl border border-dashed border-secondary-300 p-10 text-center text-secondary-500 dark:border-secondary-700">
          Loading trip briefs…
        </div>
      ) : isError ? (
        /* Never render a failed fetch as the empty state. `list()` used to
           swallow 429/503/504 into `[]`, so an unreachable service was
           indistinguishable from "no briefs exist" — that is what made a
           rate-limited demo look like briefs were not saving to the database. */
        <div className="rounded-xl border border-dashed border-danger-300 p-10 text-center dark:border-danger-800">
          <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-danger-500" />
          <h3 className="mt-2 text-base font-semibold text-secondary-900 dark:text-white">Couldn’t load trip briefs</h3>
          <p className="text-sm text-secondary-500 dark:text-secondary-400">
            {error instanceof Error ? error.message : 'The executive-protection service did not respond.'}
          </p>
          <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
            Existing briefs are unaffected — this is a read failure, not data loss.
          </p>
          <div className="mt-4 flex justify-center">
            <Button variant="outline" leftIcon={<ArrowPathIcon className="h-4 w-4" />} onClick={() => void refetch()}>
              Retry
            </Button>
          </div>
        </div>
      ) : briefs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-secondary-300 p-10 text-center dark:border-secondary-700">
          <DocumentTextIcon className="mx-auto h-10 w-10 text-secondary-400" />
          <h3 className="mt-2 text-base font-semibold text-secondary-900 dark:text-white">No trip briefs yet</h3>
          <p className="text-sm text-secondary-500 dark:text-secondary-400">
            Create a Travel Risk Brief to start generating travel-risk reports for upcoming trips.
          </p>
          <div className="mt-4 flex justify-center">
            <Button onClick={() => setShowNew(true)} leftIcon={<PlusIcon className="h-4 w-4" />}>
              New Travel Risk Brief
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {briefs.map((b) => (
            <div
              key={b.id}
              role="button"
              tabIndex={0}
              onClick={() => setActiveBriefId(b.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setActiveBriefId(b.id);
                }
              }}
              className="cursor-pointer text-left transition-transform hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-lg"
            >
              <Card className="h-full">
                <CardContent className="space-y-2 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-mono text-xs text-secondary-500">{b.briefNumber}</div>
                      <h3 className="text-base font-semibold text-secondary-900 dark:text-white">{b.title}</h3>
                      <p className="text-sm text-secondary-500 dark:text-secondary-400">{b.protectedPersonName}</p>
                    </div>
                    <span className={cn('whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold', (b.liveDataStatus === 'pending' || b.aiSummaryStatus === 'PENDING') ? STATUS_BADGES.GENERATING : STATUS_BADGES[b.status])}>
                      {(b.liveDataStatus === 'pending' || b.aiSummaryStatus === 'PENDING') ? 'RUNNING' : b.status}
                    </span>
                  </div>
                  <div className="text-xs text-secondary-500 dark:text-secondary-400">
                    {formatDate(b.departDate)} → {formatDate(b.returnDate)} · {b.purpose} · v{b.version}
                  </div>
                  <p className="line-clamp-2 text-sm text-secondary-700 dark:text-secondary-300">
                    {b.flashSummary.replace(/\*\*/g, '').split('\n')[0]}
                  </p>
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-xs text-secondary-500 dark:text-secondary-400">
                      Updated {b.updatedAt.slice(0, 10)}
                    </span>
                    {b.status !== 'APPROVED' && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Delete this trip brief? This cannot be undone.')) {
                            deleteMutation.mutate(b.id);
                          }
                        }}
                        className="text-xs text-rose-600 hover:underline"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      )}

      <NewBriefModal
        open={showNew}
        onClose={() => setShowNew(false)}
        protectedPersons={personOptions}
        onCreated={handleCreated}
      />

      <AnimatePresence>
        {activeBriefId && (
          <BriefDetailLoader briefId={activeBriefId} onClose={() => setActiveBriefId(null)} />
        )}
      </AnimatePresence>
    </div>
  );
}
